import { appendToSystemMessage, withContentFilterFlag, withContentPolicy } from './content-policy.js';

/**
 * Pure request construction for the browser LLM paths. `structured.js` owns
 * the account, deadline and transport; everything about the shape of a
 * request lives here so node tests can pin it: the three structured rungs,
 * the `/no_think` suffix, the schema instruction, the constrained JSON
 * fallback, and the Mature Content additions (the platform's filter-off flag
 * and the shared content policy). With Mature Content off, the only change
 * from the requests Vibe sent before is the `response_format` on the
 * JSON-only rung, which applies to everyone (as does the relaxed roleplay
 * guard in input-validation.js, which runs before any request is built).
 */

export const STRUCTURED_OUTPUT_TOOL = 'generate_structured_output';
export const STRUCTURED_ATTEMPT_LABELS = ['tool-choice-object', 'tool-choice-required', 'json-only-fallback'];
const LLM_TOKEN_TYPE = 'spark';
const NO_THINK_SUFFIX = '/no_think';

function appendNoThinkSuffix(messages) {
  return messages.map((msg) => {
    if (!msg || typeof msg.content !== 'string') return msg;
    const trimmed = msg.content.trimEnd();
    if (trimmed.endsWith(NO_THINK_SUFFIX)) return msg;
    return { ...msg, content: `${trimmed} ${NO_THINK_SUFFIX}` };
  });
}

function withSchemaInstruction(messages, jsonSchema, attempt) {
  const instruction = [
    'Return one JSON object matching the schema.',
    `When tools are available, call "${STRUCTURED_OUTPUT_TOOL}" exactly once with the JSON payload as arguments.`,
    'Do not output <think> tags, reasoning traces, markdown, or code fences.',
    'If you cannot call tools, return raw JSON only with no extra text.',
    `Schema: ${JSON.stringify(jsonSchema)}`,
    attempt > 1 ? 'Your previous response was invalid. Strictly follow the schema.' : null
  ].filter(Boolean).join('\n');
  return appendToSystemMessage(messages, instruction);
}

/**
 * The structured-output ladder: a forced tool call, then `tool_choice:
 * 'required'`, then a JSON-only reply. The JSON-only rung also sends the
 * schema as `response_format` so the worker's grammar stops the reply at the
 * end of the object instead of letting it run to the token cap.
 */
function structuredAttemptConfigs(temperature, maxTokens) {
  const structuredMaxTokens = Math.max(maxTokens || 0, 1600);
  return [
    {
      maxTokens: structuredMaxTokens,
      temperature: Math.min(temperature, 0.5),
      useTools: true,
      toolChoice: {
        type: 'function',
        function: { name: STRUCTURED_OUTPUT_TOOL }
      }
    },
    {
      maxTokens: Math.max(structuredMaxTokens, 2600),
      temperature: 0.2,
      useTools: true,
      toolChoice: 'required'
    },
    {
      maxTokens: Math.max(structuredMaxTokens, 2200),
      temperature: 0.1,
      useTools: false
    }
  ];
}

/** One request per rung, in STRUCTURED_ATTEMPT_LABELS order. */
export function buildStructuredRequests({ model, messages, jsonSchema, temperature, maxTokens, matureContentEnabled = false }) {
  const policyMessages = withContentPolicy(messages, matureContentEnabled);
  return structuredAttemptConfigs(temperature, maxTokens).map((attemptConfig, index) => {
    const request = {
      model,
      messages: appendNoThinkSuffix(withSchemaInstruction(policyMessages, jsonSchema, index + 1)),
      temperature: attemptConfig.temperature,
      max_tokens: attemptConfig.maxTokens,
      tokenType: LLM_TOKEN_TYPE,
      think: false
    };
    if (attemptConfig.useTools) {
      request.tools = [{
        type: 'function',
        function: { name: STRUCTURED_OUTPUT_TOOL,
          description: 'Return structured JSON response matching the provided schema', parameters: jsonSchema }
      }];
      request.tool_choice = attemptConfig.toolChoice;
    } else {
      request.response_format = {
        type: 'json_schema',
        json_schema: { name: STRUCTURED_OUTPUT_TOOL, schema: jsonSchema, strict: true }
      };
    }
    return withContentFilterFlag(request, matureContentEnabled);
  });
}

const DESCRIBE_IMAGE_SYSTEM_PROMPT = [
  'You are an expert at writing image-generation prompts.',
  'Look at the image and write one detailed image-generation prompt that would recreate a similar image.',
  'Cover: the main subject and what it is doing, the setting, the art style or medium',
  '(photograph, oil painting, anime, 3D render, watercolor, etc.), composition and framing,',
  'lighting, color palette, mood, and any distinctive details.',
  'Respond with the prompt only, as one paragraph of 60-150 words.',
  'No preamble, no quotes, no markdown, no reasoning.'
].join(' ');

/** Vision request: a plain text completion describing the image as a prompt. */
export function buildDescribeImageRequest({ model, imageDataUrl, matureContentEnabled = false }) {
  const messages = [
    { role: 'system', content: DESCRIBE_IMAGE_SYSTEM_PROMPT },
    {
      role: 'user',
      content: [
        { type: 'text', text: `Describe this image as an image-generation prompt. ${NO_THINK_SUFFIX}` },
        { type: 'image_url', image_url: { url: imageDataUrl } }
      ]
    }
  ];
  return withContentFilterFlag({
    model,
    messages: withContentPolicy(messages, matureContentEnabled),
    temperature: 0.4,
    max_tokens: 1600,
    tokenType: LLM_TOKEN_TYPE,
    think: false
  }, matureContentEnabled);
}
